"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BillingMeterSchema = exports.BillingSkuSchema = exports.SKU_AUDITED_FIELDS = exports.SKU_HISTORY_CAP = exports.BillingSkuHistoryEntrySchema = exports.RENTAL_KINDS = exports.FINANCE_TYPES = exports.METER_BASES = exports.ENFORCEMENT_MODES = exports.SKU_SCOPES = exports.SKU_TYPES = void 0;
exports.appendSkuHistory = appendSkuHistory;
exports.diffSkuForHistory = diffSkuForHistory;
exports.tokensFor = tokensFor;
const zod_1 = require("zod");
const hardwareCatalog_1 = require("./hardwareCatalog");
/**
 * BILLING CATALOG — the single definition of a price.
 *
 * A SKU is the only place an amount and a Stripe price id live. Deals compose
 * SKUs; they do not define prices. See §4.1 / §15 of the unified billing design.
 */
exports.SKU_TYPES = ['plan', 'package', 'device_finance', 'token_pack'];
exports.SKU_SCOPES = ['tenant', 'location'];
exports.ENFORCEMENT_MODES = ['suspend_all', 'suspend_feature', 'collections_only'];
exports.METER_BASES = ['per_call', 'per_usd_cost', 'per_minute', 'per_1k_tokens'];
exports.FINANCE_TYPES = ['installment', 'lease', 'rental'];
// How a rental runs: month-to-month until cancelled, or a fixed term that
// ends (return or renew). Only meaningful when saleMode is 'rental'.
exports.RENTAL_KINDS = ['open_ended', 'fixed_term'];
/**
 * ONE ENTRY IN A SKU's EDIT HISTORY (2026-08-27 financing-policy spec).
 *
 * Mirrors `DealPricingHistoryEntrySchema` in `deal.ts` — the platform
 * already has one audit-trail shape and this is deliberately the same one,
 * so a reader that can render a deal's pricing history can render this.
 *
 * `changes` is a plain field-level before/after list rather than a free-text
 * note: "monthly went from $52.00 to $49.00" is answerable months later,
 * "edited pricing" is not. Values are stringified by the writer so a cents
 * amount, a term count and an enum all render through one code path.
 */
exports.BillingSkuHistoryEntrySchema = zod_1.z.object({
    at: zod_1.z.number(),
    by: zod_1.z.string(),
    /** What kind of change this was — drives the icon/wording in the timeline. */
    action: zod_1.z.enum(['created', 'updated', 'retired', 'reoffered']),
    changes: zod_1.z.array(zod_1.z.object({
        field: zod_1.z.string(),
        from: zod_1.z.string().optional(),
        to: zod_1.z.string().optional(),
    })).optional(),
});
/** Same cap and rationale as `PRICING_HISTORY_CAP` — an audit trail that cannot grow unbounded. */
exports.SKU_HISTORY_CAP = 20;
/**
 * Appends one history entry, keeping only the most recent `SKU_HISTORY_CAP`.
 */
function appendSkuHistory(existing, entry) {
    return [...(existing ?? []), entry].slice(-exports.SKU_HISTORY_CAP);
}
/**
 * The fields worth auditing on a SKU, and how to label them in the timeline.
 * Deliberately a WHITELIST: `updatedAt` changes on every write and would
 * make every entry noise, and `stripePriceId` is re-minted as a CONSEQUENCE
 * of a price change rather than a change an operator made.
 */
exports.SKU_AUDITED_FIELDS = {
    name: 'Name',
    amountCents: 'Amount',
    termMonths: 'Term (months)',
    downPaymentCents: 'Down payment',
    depositCents: 'Deposit',
    minCommitmentMonths: 'Minimum commitment',
    resellerPriceCents: 'Reseller price',
    resellerMonthlyCents: 'Reseller monthly',
    warrantyMonths: 'Warranty (months)',
    rentalKind: 'Rental kind',
    saleMode: 'Sale mode',
    financeType: 'Finance type',
    status: 'Status',
    agreementTemplateId: 'Agreement template',
    agreementTemplateVersion: 'Agreement version',
    interval: 'Billing interval',
    enforcement: 'Enforcement',
};
/**
 * Field-level diff between a stored SKU and the row about to replace it,
 * limited to `SKU_AUDITED_FIELDS`. Nested `defaultPolicy` and
 * `affiliateCommission` are compared as a whole and reported under one
 * label each — an operator thinks of "the commission", not of
 * `affiliateCommission.percent`.
 *
 * Returns `[]` when nothing audited moved, which is the signal NOT to write
 * a history entry at all (mirrors `pricingChanged`'s role for deals).
 */
function diffSkuForHistory(prev, next) {
    const show = (v) => {
        if (v === undefined || v === null)
            return undefined;
        if (typeof v === 'object')
            return JSON.stringify(v);
        return String(v);
    };
    const changes = [];
    for (const [field, label] of Object.entries(exports.SKU_AUDITED_FIELDS)) {
        const a = show(prev?.[field]);
        const b = show(next?.[field]);
        if (a !== b)
            changes.push({ field: label, ...(a !== undefined ? { from: a } : {}), ...(b !== undefined ? { to: b } : {}) });
    }
    for (const [field, label] of [['defaultPolicy', 'Grace / enforcement'], ['affiliateCommission', 'Affiliate commission']]) {
        const a = show(prev?.[field]);
        const b = show(next?.[field]);
        if (a !== b)
            changes.push({ field: label, ...(a !== undefined ? { from: a } : {}), ...(b !== undefined ? { to: b } : {}) });
    }
    return changes;
}
exports.BillingSkuSchema = zod_1.z.object({
    skuId: zod_1.z.string().min(1),
    type: zod_1.z.enum(exports.SKU_TYPES),
    scope: zod_1.z.enum(exports.SKU_SCOPES),
    packageId: zod_1.z.string().optional(),
    /** Package tiers (2026-10-11) — copied from the package by catalogSync. Absent = ungrouped. */
    familyId: zod_1.z.string().optional(),
    tierRank: zod_1.z.number().int().optional(),
    tierLabel: zod_1.z.string().optional(),
    name: zod_1.z.string().min(1),
    description: zod_1.z.string().optional(),
    stripePriceId: zod_1.z.object({ dev: zod_1.z.string(), prod: zod_1.z.string() }),
    amountCents: zod_1.z.number().int().min(0),
    interval: zod_1.z.enum(['month', 'year', 'once']),
    tokenGrantMonthly: zod_1.z.number().int().min(0).default(0),
    termMonths: zod_1.z.number().int().positive().optional(),
    financeType: zod_1.z.enum(exports.FINANCE_TYPES).optional(),
    // HARDWARE LINK (all optional — absent on every SKU that predates the hardware
    // catalog). Exactly one of modelId | bundleId is meaningful; neither is required.
    modelId: zod_1.z.string().optional(),
    bundleId: zod_1.z.string().optional(),
    saleMode: zod_1.z.enum(hardwareCatalog_1.SALE_MODES).optional(),
    // FINANCING WIZARD FIELDS (all optional — additive, absent on every SKU
    // that predates the wizard). See inventory-financing-suite mockup screen 5.
    //
    // Three price tiers: `amountCents` above is the RETAIL price a customer
    // pays; `resellerPriceCents`/`resellerMonthlyCents` are what a reseller is
    // charged for the same offer (their margin is the spread to retail).
    // `economics.unitCostCents` on the hardware model (hardwareCatalog.ts)
    // remains the read-only landed cost — never duplicated here.
    resellerPriceCents: zod_1.z.number().int().min(0).optional(),
    resellerMonthlyCents: zod_1.z.number().int().min(0).optional(),
    // W5 review I5 — additive. Financing-wizard screen 5's "Down payment"
    // input, collected up front and subtracted from the financed principal.
    // Absent/undefined means no down payment, same as every SKU created
    // before this field existed.
    downPaymentCents: zod_1.z.number().int().min(0).optional(),
    // RENTAL FIELDS (all optional — additive, absent on every SKU that
    // predates the rent option). Only meaningful when saleMode is 'rental'.
    // 'open_ended' bills monthly until cancelled (no termMonths, no schedule);
    // 'fixed_term' requires termMonths and rides the same D8-safe termed
    // schedule an installment does. An absent rentalKind on a rental SKU is
    // read as open-ended — the kind that needs NO extra field to be valid.
    rentalKind: zod_1.z.enum(exports.RENTAL_KINDS).optional(),
    // Open-ended rentals only: the months the customer commits to before they
    // may cancel. 0/absent = cancel any time. Informational for the agreement
    // (principal = commitment × monthly) — enforcement is contractual, not a
    // billing-schedule mechanism.
    minCommitmentMonths: zod_1.z.number().int().min(0).optional(),
    // Refundable security deposit, collected once at checkout on the same
    // one-time invoice an outright purchase uses. Refund on return is recorded
    // on the unit's agreement, never derived.
    depositCents: zod_1.z.number().int().min(0).optional(),
    // The one-time Stripe price for `depositCents`, minted by billing-service's
    // generatePrice alongside the SKU's own recurring price. Per-stage, same
    // convention as stripePriceId. Absent when depositCents is absent/0.
    stripeDepositPriceId: zod_1.z.object({ dev: zod_1.z.string(), prod: zod_1.z.string() }).optional(),
    // COMMISSION PLAN OVERRIDE (2026-08-27) — tier one of the resolution
    // cascade in `commissionPlan.ts`: offer override -> partner pin -> deal's
    // plan -> global default. Absent (the norm) means this offer simply inherits
    // whichever plan resolves for the partner making the sale; set it only for a
    // genuinely offer-specific arrangement, e.g. clearance stock.
    //
    // Additive and never required: every SKU created before this field existed
    // resolves exactly as it did, through the tiers below it.
    commissionPlanId: zod_1.z.string().optional(),
    // @deprecated 2026-08-27 — superseded by `commissionPlanId` + CommissionPlan
    // rules. NEVER read by any service: this was written by the financing wizard
    // and no pay path ever consumed it. Kept so stored rows still parse and so
    // an edit round-trip does not wipe it; do not add new writers.
    affiliateCommission: zod_1.z.object({
        mode: zod_1.z.enum(['inherit_deal', 'custom_percent', 'none']),
        // Required only for 'custom_percent' — enforced by the refine below
        // rather than a discriminated union, so a stored row missing it (should
        // one ever exist) still parses as 'unset' instead of failing entirely.
        percent: zod_1.z.number().min(0).max(100).optional(),
    }).optional(),
    // The exact document-governance template + version this offer's agreement
    // is bound to. `agreementTemplateVersion` mirrors `DocumentVersionSchema`'s
    // `versionNo` (documents.ts) — a NUMBER, not a semver string — because a
    // signature binds the exact published version signed, and the version
    // chain is numbered, not semver'd.
    agreementTemplateId: zod_1.z.string().optional(),
    agreementTemplateVersion: zod_1.z.number().int().positive().optional(),
    /**
     * EVERY DOCUMENT THAT MUST BE SIGNED FOR THIS OFFER.
     *
     * The pair above binds ONE document at ONE pinned version, which is the
     * shape this started with and the shape that caused the trouble: publishing a
     * new version meant updating every SKU by hand, and an order whose devices
     * disagreed was refused outright.
     *
     * This is the replacement. A LIST, because a sale can require more than one
     * signature (a rental agreement and a data-processing addendum), and
     * DOCUMENT IDS ONLY, with no version: checkout instantiates whatever version
     * is published at that moment and records on the envelope which one was
     * signed. There is then no version to keep in sync anywhere, and what was
     * agreed is still exactly known.
     *
     * ADDITIVE. `agreementTemplateId` is untouched and still honoured — a SKU
     * carrying only the old pair behaves exactly as it did. Readers should take
     * this list when present and fall back to the single field otherwise.
     */
    agreementDocumentIds: zod_1.z.array(zod_1.z.string().min(1)).optional(),
    /**
     * OFFER THIS TO EVERY TENANT, NOT ONLY ON A DEAL.
     *
     * A device offer reaches a tenant one of two ways: their deal names it, or it
     * is on the open shelf. This is the shelf.
     *
     * The distinction matters because stock and sellability are NOT the same
     * thing. Hardware held back for negotiated deals — a unit reserved for a
     * partner, a model being wound down, anything an operator does not want a
     * stranger buying — stays in inventory and stays orderable through a deal,
     * while never appearing in the marketplace. Absent means exactly that:
     * invisible unless a deal names it, which is how every offer written before
     * this behaved.
     *
     * Set per OFFER, not per model: a K10 rented, financed and bought outright
     * are three SKUs at three prices, and "sell this one" is a decision about a
     * price, not about a piece of plastic.
     */
    marketplace: zod_1.z.boolean().optional(),
    warrantyMonths: zod_1.z.number().int().min(0).optional(),
    // The ONLY early-payoff policy today: pay the remaining balance, no
    // penalty (locked product decision). A single-value enum rather than a
    // boolean so a future second policy is additive, not a breaking rename.
    earlyPayoff: zod_1.z.enum(['remaining_no_penalty']).optional(),
    // What happens on missed payment for a financed offer. Reuses
    // ENFORCEMENT_MODES — the same three modes every other enforcement path in
    // the catalog already uses — rather than inventing a parallel vocabulary.
    defaultPolicy: zod_1.z.object({
        graceDays: zod_1.z.number().int().min(0),
        enforcement: zod_1.z.enum(exports.ENFORCEMENT_MODES),
    }).optional(),
    cancellable: zod_1.z.boolean(),
    enforcement: zod_1.z.enum(exports.ENFORCEMENT_MODES),
    /**
     * What actually happens on default, as a list of action block ids
     * (`ACTION_BLOCKS_V1` in `actionBlocks.ts`).
     *
     * ADDITIVE beside `enforcement`, which stays exactly as it was and is still
     * read by `stateFor()`. Absent means "derive from `enforcement`" via
     * `blocksForEnforcement()`, so every policy stored before this existed
     * keeps its exact present meaning. When present, writers must also keep
     * `enforcement` in sync via `enforcementForBlocks()` so a reader that only
     * knows the old field never meets a value it cannot parse.
     */
    enforcementActions: zod_1.z.array(zod_1.z.string()).optional(),
    dependencies: zod_1.z.array(zod_1.z.string()).default([]),
    status: zod_1.z.enum(['active', 'deprecated']),
    displayOrder: zod_1.z.number().int(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
    // ---------------------------------------------------------------------
    // PROVENANCE (2026-08-27 financing-policy spec) — who built this offer,
    // who last changed it, and what changed. The SKU catalog was the only
    // money-touching path in billing-service that recorded no actor at all,
    // while every other one (payment methods, financing cancellation,
    // subscription sync) already did.
    //
    // These MUST live on the schema, not just in the handler: this schema is
    // a plain z.object (no `.passthrough()`, unlike DealSchema), so Zod's
    // strip mode would silently discard any audit field a handler wrote.
    //
    // All OPTIONAL — every SKU minted before this shipped carries none of
    // them, and an added field is never required (shared-types rule).
    // ---------------------------------------------------------------------
    createdBy: zod_1.z.string().optional(),
    updatedBy: zod_1.z.string().optional(),
    /** Append-only, capped at the most recent `SKU_HISTORY_CAP` — see `appendSkuHistory`. */
    history: zod_1.z.array(exports.BillingSkuHistoryEntrySchema).optional(),
}).refine(
// A term is only meaningful for term-based sale modes. An outright purchase and a
// device included free with a plan have no term, and an OPEN-ENDED rental
// deliberately has none — it bills until cancelled. This accepts STRICTLY MORE
// than the previous rule, so it cannot invalidate any existing row.
(s) => s.type !== 'device_finance'
    || s.saleMode === 'outright'
    || s.saleMode === 'included'
    || (s.saleMode === 'rental' && s.rentalKind !== 'fixed_term')
    || typeof s.termMonths === 'number', { message: 'termMonths is required for term-based device_finance SKUs', path: ['termMonths'] }).refine(
// A fixed-term rental with no term would silently become open-ended — it
// would bill forever instead of ending at month N. Never let the two kinds
// blur at the schema.
(s) => !(s.saleMode === 'rental' && s.rentalKind === 'fixed_term') || typeof s.termMonths === 'number', { message: 'termMonths is required when rentalKind is "fixed_term"', path: ['termMonths'] }).refine(
// W5 review I14 — the field comment above already claimed this was
// "enforced by the refine below"; it wasn't. custom_percent with no rate
// parsed silently, and any writer other than the wizard (a script, a
// hand-edited row, a future edit UI) could produce a commission whose
// rate the affiliate payout path would read as undefined/NaN.
(s) => s.affiliateCommission?.mode !== 'custom_percent' || typeof s.affiliateCommission?.percent === 'number', { message: 'affiliateCommission.percent is required when mode is "custom_percent"', path: ['affiliateCommission', 'percent'] });
exports.BillingMeterSchema = zod_1.z.object({
    meterId: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    category: zod_1.z.enum(['ai', 'voice', 'mcp', 'media']),
    basis: zod_1.z.enum(exports.METER_BASES),
    multiplier: zod_1.z.number().positive(),
    minTokens: zod_1.z.number().int().min(0).default(1),
    requiresSku: zod_1.z.string().optional(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
});
/**
 * Tokens burned for one metered event. Mirrors the legacy pos-agents formula
 * (ceil(costUsd * burnRate * 100)) so migrated tenants see identical charges.
 */
function tokensFor(meter, multiplier, input) {
    if (multiplier === 0)
        return 0;
    let raw = 0;
    switch (meter.basis) {
        case 'per_usd_cost':
            raw = Math.ceil((input.costUsd ?? 0) * multiplier * 100);
            break;
        case 'per_call':
        case 'per_minute':
            raw = Math.ceil((input.units ?? 0) * multiplier);
            break;
        case 'per_1k_tokens':
            raw = Math.ceil((input.units ?? 0) / 1000) * multiplier;
            break;
    }
    return Math.max(meter.minTokens, raw);
}
//# sourceMappingURL=billingCatalog.js.map